import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Paperclip, BookOpen } from "lucide-react";
import OneDrivePicker from "../../../../components/ThirdPartyServices/OneDrivePicker";
import GoogleDrivePickers from "../../../../components/ThirdPartyServices/GoogleDrivePickers";
import { useUsersContext } from "../../../../context/usersContext";
import { useUserInfo } from "../../../../context/user";
import { useMediaLibraryFiles, useChatUploadFiles } from "../../../../queries/chatActions";
import FileTooBigModal from "./FileTooBigModal";

// The composer can't carry a 32MB+ attachment, but the Knowledge Base
// dropzone already accepts up to 512MB (components/uploadFiles/MyDropzone.jsx)
// — FileTooBigModal redirects anything over this instead of just rejecting it.
const MAX_CHAT_FILE_BYTES = 32 * 1024 * 1024;

// Inline brand glyphs (Lovable AttachMenu pattern) so we don't ship logo PNGs.
const GoogleDriveIcon = () => (
  <svg viewBox="0 0 87.3 78" className="h-4 w-4 shrink-0" aria-hidden>
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
  </svg>
);

const OneDriveIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" aria-hidden fill="#0078D4">
    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
  </svg>
);

const MENU_ROW =
  "flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium cursor-pointer hover:bg-accent hover:text-accent-foreground";

function Index({
  classname,
  chatId,
  onFileSelect,
  onRunNow,
  onAutomate,
}) {
  const { agencyInfo, contextuploadFilefrominput } = useUsersContext();
  const [successModel, setSuccessModel] = useState(false);
  const [modelContent, setModelContent] = useState("");
  const [toggleOption, setToggleOption] = useState(false);
  const [selectFileModel, setSelectFileModel] = useState(false);
  const [mediaLibraryModel, setMediaLibraryModel] = useState(false);
  const [tempSelectedFiles, setTempSelectedFiles] = useState([]);
  const [files, setFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [spinnerLoading, setSpinnerLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [convertedFiles, setConvertedFiles] = useState([]);
  const [mediaLibraryData, setMediaLibraryData] = useState([]);
  const [tempSelectedFilesMedia, setTempSelectedFilesMedia] = useState([]);
  const [searchMediaTerm, setSearchMediaTerm] = useState("");
  const [userInfo, , , , userActiveLocation] = useUserInfo();

  const filteredMediaLibraryData = mediaLibraryData?.filter((item) =>
    item.name.toLowerCase().includes(searchMediaTerm.toLowerCase()),
  );

  const handleCloseModal = () => {
    setMediaLibraryModel(false); // Close the modal
    setSearchMediaTerm(""); // Clear the search term
  };

  // Helper function to download and convert files
  const downloadFiles = async (files) => {
    return Promise?.all(
      files?.map(async (file) => {
        try {
          const response = await fetch(file.url);
          const blob = await response.blob();
          return new File([blob], file.name, { type: file.mimeType });
        } catch (error) {
          console.error("Error downloading file:", error);
          return null;
        }
      }),
    ).then((results) => results.filter(Boolean));
  };

  const { data: mediaLibraryFiles, error: mediaLibraryError } =
    useMediaLibraryFiles(userActiveLocation?.id);

  useEffect(() => {
    if (mediaLibraryError) {
      console.error("Error fetching media files:", mediaLibraryError);
    }
  }, [mediaLibraryError]);

  useEffect(() => {
    if (mediaLibraryFiles === undefined) return;

    // Set the media library data
    setMediaLibraryData(mediaLibraryFiles);

    // Process and convert files (same fetch()-based download step as before,
    // including its known "undefined is not iterable" crash when the list
    // response has no `files` field)
    downloadFiles(
      mediaLibraryFiles?.map((file) => ({
        url: file.url,
        name: file.name,
        mimeType: file.contentType, // Assuming contentType corresponds to mimeType
      })),
    )
      .then(setConvertedFiles)
      .catch((error) => console.error("Error fetching media files:", error));
  }, [mediaLibraryFiles]);

  const handleFileSelectMedia = (file) => {
    setTempSelectedFilesMedia((prev) => {
      const isAlreadySelected = prev.some(
        (selectedFile) => selectedFile.id === file.id,
      );
      if (isAlreadySelected) {
        // Remove the file if already selected
        return prev.filter((selectedFile) => selectedFile.id !== file.id);
      }
      // Add the file if not selected
      return [...prev, file];
    });
  };

  const handleDoneClickMedia = async () => {
    const assistant = [{ value: "asst_Cwkwlf4oUGjIWmuOrUiBqgx9" }];

    onFileSelect(convertedFiles);
    setMediaLibraryModel(false);
    setTempSelectedFilesMedia([]);
  };

  const handleAction = () => {
    setDisabled(true);
    setTimeout(() => setDisabled(false), 3000); // Disable for 3 seconds
  };

  const filteredData = Array.isArray(files)
    ? files.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    : [];

  const filteredFiles = filteredData.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleFileSelect = (file) => {
    setTempSelectedFiles((prevSelectedFiles) => {
      const isAlreadySelected = prevSelectedFiles.some(
        (selectedFile) => selectedFile.id === file.id,
      );
      if (isAlreadySelected) {
        return prevSelectedFiles.filter(
          (selectedFile) => selectedFile.id !== file.id,
        );
      } else {
        return [...prevSelectedFiles, file];
      }
    });
  };

  const handleDoneClick = () => {
    onFileSelect(tempSelectedFiles);
    setSelectFileModel(false);
    setToggleOption(false);
    setTempSelectedFiles([]);
  };

  const { data: uploadFilesData } = useChatUploadFiles(
    userActiveLocation?.id,
    userInfo?._id,
    selectFileModel === true,
  );

  useEffect(() => {
    if (uploadFilesData !== undefined) {
      setFiles(uploadFilesData);
      setSpinnerLoading(false);
    }
  }, [uploadFilesData]);

  const threeDotRef = useRef(null);
  const hiddenFileInputRef = useRef(null);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  // CU-123yxuaptmf: the menu is portaled to <body> and placed via a measured
  // fixed position so it can't get sliced by a scrollable/overflow-hidden
  // ancestor (this composer is reused inside modals with a capped height).
  // Direction flips to whichever side of the button actually has room.
  useLayoutEffect(() => {
    if (!toggleOption || !anchorRef.current) {
      setMenuPos(null);
      return;
    }
    const MENU_HEIGHT_ESTIMATE = 200;
    const MENU_WIDTH = 256;
    const GAP = 8;

    const updatePosition = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < MENU_HEIGHT_ESTIMATE && spaceAbove > spaceBelow;

      let left = rect.right - MENU_WIDTH;
      if (left + MENU_WIDTH > window.innerWidth - 8) {
        left = window.innerWidth - MENU_WIDTH - 8;
      }
      left = Math.max(8, left);

      setMenuPos({
        left,
        top: openUp ? rect.top - GAP : rect.bottom + GAP,
        openUp,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [toggleOption]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const insideButton =
        threeDotRef.current && threeDotRef.current.contains(event.target);
      const insideMenu =
        menuRef.current && menuRef.current.contains(event.target);
      if (!insideButton && !insideMenu) {
        setToggleOption(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [threeDotRef]);

  const filesFromThirdParty = async (files) => {
    var response = await contextuploadFilefrominput(
      files,
      chatId,
      setSpinnerLoading,
    );
    if (response?.data?.success === true) {
      onFileSelect(response?.data?.data);
    }
  };

  const [oversizedQueue, setOversizedQueue] = useState([]);

  const handleNativeFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (picked.length === 0) return;
    const withinLimit = picked.filter((f) => f.size <= MAX_CHAT_FILE_BYTES);
    const oversized = picked.filter((f) => f.size > MAX_CHAT_FILE_BYTES);
    if (withinLimit.length > 0) onFileSelect(withinLimit);
    if (oversized.length > 0) setOversizedQueue((prev) => [...prev, ...oversized]);
  };

  return (
    <div ref={threeDotRef}>
      <input
        ref={hiddenFileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp,text/plain,text/markdown,text/csv,.txt,.md,.csv,application/pdf,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,.xlsx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.ppt,.pptx,audio/mpeg,audio/wav,audio/mp4,audio/x-m4a,audio/flac,.mp3,.wav,.m4a,.flac,video/mp4,video/mpeg,video/webm,.mp4,.mpeg,.webm,application/json,.json"
        style={{ display: "none" }}
        onChange={handleNativeFilePick}
      />
      <FileTooBigModal
        file={oversizedQueue[0]}
        open={oversizedQueue.length > 0}
        onOpenChange={(next) => {
          if (!next) setOversizedQueue((prev) => prev.slice(1));
        }}
        onFileSelect={onFileSelect}
        onRunNow={onRunNow}
        onAutomate={onAutomate}
      />
      {selectFileModel && (
        <div
          className="flex items-center justify-center fixed z-[9999] left-0 top-0 w-full h-full overflow-auto bg-[rgba(0,0,0,0.4)]"
          onClick={() => setSelectFileModel(false)}
        >
          <div
            className="bg-card m-auto py-8 px-9 border border-border w-[600px] rounded-[10px] relative max-h-[534px] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <p
              className="absolute right-1 w-[12px] top-[10px] h-[12px] cursor-pointer"
              onClick={() => setSelectFileModel(false)}
            >
              X
            </p>

            <div className="mb-8 flex justify-between items-center">
              <b className="text-foreground">Select a file</b>

              <input
                type="search"
                placeholder="Search"
                className="outline-none px-2 py-1.5 border border-border rounded-[5px] w-1/2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="max-h-[350px] overflow-auto">
              <div className="flex pl-7 pr-6">
                <p className="w-[33.33%] text-[14px] font-[500] mb-4">
                  File Name
                </p>
                <p className="text-center w-[33.33%] text-[14px] mb-4 font-[500]">
                  Date created
                </p>
                <p className="w-[33.33%] text-[14px] font-[500] mb-4"></p>
              </div>

              <div>
                {filteredFiles.length > 0 ? (
                  filteredFiles.map((item, index) => (
                    <div
                      className="flex rounded-[5px] border border-border pl-7 pr-6 items-center mb-3"
                      key={index}
                    >
                      <p className="w-[33.33%] text-[12px] font-[400] m-0 px-0 py-5">
                        {item.name.length > 15
                          ? item.name.slice(0, 15) + "..."
                          : item.name}
                      </p>
                      <p className="w-[33.33%] text-[12px] font-[400] m-0 px-0 py-5 text-center">
                        {new Date(item.created_at).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </p>
                      <div className="w-[33.33%] items-start flex justify-end ">
                        <input
                          type="checkbox"
                          className="w-5 h-5 ml-auto bg-card text-foreground cursor-pointer"
                          onChange={() => handleFileSelect(item)}
                          checked={tempSelectedFiles.some(
                            (selectedFile) => selectedFile.id === item.id,
                          )}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="flex items-center mb-4">No files found</p>
                )}
              </div>
            </div>

            <button
              className="bg-primary text-primary-foreground rounded-[5px] font-[500] text-[14px] leading-[16.94px] text-center w-full p-[12px]"
              onClick={handleDoneClick}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {mediaLibraryModel && (
        <div
          className="flex items-center justify-center fixed z-[99999] left-0 top-0 w-full h-full bg-black overflow-auto"
          onClick={handleCloseModal}
        >
          <div
            className="bg-card m-auto py-8 px-1.5 border border-border w-[600px] rounded-[10px] relative max-h-[534px] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <p
              className="absolute right-1 w-[12px] top-[10px] h-[12px] cursor-pointer"
              onClick={handleCloseModal}
            >
              X
            </p>

            <div className="mb-8 flex justify-between items-center">
              <b className="text-foreground">Select a file</b>

              <input
                type="search"
                placeholder="Search"
                className="outline-none px-2 py-1.5 border border-border rounded-[5px] w-1/2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="max-h-[350px] overflow-auto">
              <div className="flex pl-7 pr-6">
                <p className="w-[33.33%] text-[14px] font-[500] mb-4">
                  File Name
                </p>
                <p className="text-center w-[33.33%] text-[14px] mb-4 font-[500]">
                  Date created
                </p>
                <p className="w-[33.33%] text-[14px] font-[500] mb-4"></p>
              </div>

              <div>
                {filteredMediaLibraryData?.length > 0 ? (
                  filteredMediaLibraryData?.map((item, index) => (
                    <div
                      className="flex rounded-[5px] border border-border pl-7 pr-6 items-center mb-3"
                      key={index}
                    >
                      <p className="w-[33.33%] text-[12px] font-[400] m-0 px-0 py-5">
                        {item.name.length > 15
                          ? item.name.slice(0, 15) + "..."
                          : item.name}
                      </p>
                      <p className="w-[33.33%] text-[12px] font-[400] m-0 px-0 py-5 text-center">
                        {new Date(item.created_at).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </p>
                      <div className="w-[33.33%] items-start flex justify-end ">
                        <input
                          type="checkbox"
                          className="w-5 h-5 ml-auto bg-card text-foreground cursor-pointer"
                          onChange={() => handleFileSelect(item)}
                          checked={tempSelectedFiles.some(
                            (selectedFile) => selectedFile.id === item.id,
                          )}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="flex items-center mb-4">No files found</p>
                )}
              </div>
            </div>

            <button
              className="bg-primary text-primary-foreground rounded-[5px] font-[500] text-[14px] leading-[16.94px] text-center w-full p-[12px]"
              onClick={handleDoneClick}
            >
              Done
            </button>
          </div>
        </div>
      )}

      <div className="relative" ref={anchorRef}>
        <button
          type="button"
          aria-label="Attach file"
          onClick={() => {
            setToggleOption(!toggleOption);
            handleAction();
          }}
          className={`w-9 h-9 inline-flex items-center justify-center rounded-lg ${toggleOption ? "text-foreground" : "text-muted-foreground"} hover:text-foreground ${classname || ""}`}
        >
          {spinnerLoading ? (
            <div className="w-4 h-4 border-2 border-muted !border-t-foreground rounded-full animate-spin duration-[0.6s] linear"></div>
          ) : (
            <Paperclip size={18} />
          )}
        </button>

        {toggleOption &&
          menuPos &&
          createPortal(
            <div
              ref={menuRef}
              style={{
                position: "fixed",
                top: menuPos.top,
                left: menuPos.left,
                transform: menuPos.openUp ? "translateY(-100%)" : undefined,
              }}
              className="z-50 w-64 rounded-md border border-border bg-popover p-1.5 text-popover-foreground shadow-md"
            >
              <div className={MENU_ROW}>
                <GoogleDriveIcon />
                <GoogleDrivePickers
                  disabled={disabled}
                  files_send_to_parent={filesFromThirdParty}
                  setSpinnerLoading={setSpinnerLoading}
                />
              </div>
              <div className={MENU_ROW}>
                <OneDriveIcon />
                <OneDrivePicker files_send_to_parent={filesFromThirdParty} />
              </div>
              <div
                onClick={() => {
                  setSelectFileModel(!selectFileModel);
                  setToggleOption(false);
                }}
                className={MENU_ROW}
              >
                <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span>Add from Knowledge</span>
              </div>
              <div role="separator" className="my-1 h-px bg-border" />
              <div
                onClick={() => {
                  setToggleOption(false);
                  hiddenFileInputRef.current?.click();
                }}
                className={MENU_ROW}
              >
                <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span>Add files and photos</span>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}

export default Index;
